import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Eye, Users, FileText, Download, LockOpen, ShieldCheck } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [7, 30, 90];
const CONVERSION_META = {
  apply_submitted: { label: 'Applications', icon: FileText, color: 'text-violet-600 bg-violet-50' },
  deck_downloaded: { label: 'Deck downloads', icon: Download, color: 'text-sky-600 bg-sky-50' },
  data_room_unlocked: { label: 'Data rooms unlocked', icon: LockOpen, color: 'text-emerald-600 bg-emerald-50' },
};

const Kpi = ({ id, label, value, icon: Icon, color }) => (
  <Card className="border border-slate-200 shadow-sm" data-testid={`analytics-kpi-${id}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${color}`}><Icon className="w-4 h-4" /></div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-slate-500 truncate">{label}</p>
        <p className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`analytics-kpi-${id}-value`}>{value.toLocaleString()}</p>
      </div>
    </CardContent>
  </Card>
);

const BarList = ({ id, title, items, total }) => (
  <Card className="border border-slate-200 shadow-sm" data-testid={`analytics-list-${id}`}>
    <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</CardTitle></CardHeader>
    <CardContent className="pt-0 space-y-2">
      {items.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">No data yet</p>}
      {items.map((it) => {
        const pct = total ? Math.round((it.count / total) * 100) : 0;
        return (
          <div key={it.name} className="relative" data-testid={`analytics-list-${id}-row`}>
            <div className="absolute inset-y-0 left-0 rounded bg-indigo-50" style={{ width: `${Math.max(pct, 2)}%` }} />
            <div className="relative flex items-center justify-between px-2 py-1.5 text-xs">
              <span className="truncate text-slate-700 font-medium">{it.name}</span>
              <span className="text-slate-500 shrink-0 ml-3 tabular-nums">{it.count.toLocaleString()} <span className="text-slate-400">· {pct}%</span></span>
            </div>
          </div>
        );
      })}
    </CardContent>
  </Card>
);

export default function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    axios.get(`${API}/admin/analytics?days=${days}`, { withCredentials: true })
      .then(r => { if (alive) setData(r.data); })
      .catch(e => { if (alive) setError(e?.response?.data?.detail || 'Could not load analytics'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const fmtDay = (d) => { const [, m, dd] = d.split('-'); return `${dd}/${m}`; };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="analytics-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Website Analytics</h1>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5" data-testid="analytics-privacy-note">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Privacy-friendly first-party tracking — no cookies, no third parties. Admin pages excluded.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5" data-testid="analytics-range-selector">
          {RANGES.map(r => (
            <Button key={r} size="sm" variant={days === r ? 'default' : 'ghost'} className="h-7 px-3 text-xs" onClick={() => setDays(r)} data-testid={`analytics-range-${r}`}>{r}d</Button>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-rose-600" data-testid="analytics-error">{error}</p>}
      {loading && !data && <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}

      {data && (
        <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <Kpi id="pageviews" label="Page views" value={data.pageviews} icon={Eye} color="text-indigo-600 bg-indigo-50" />
            <Kpi id="visitors" label="Unique visitors" value={data.visitors} icon={Users} color="text-amber-600 bg-amber-50" />
            {Object.entries(data.conversions || {}).map(([ev, n]) => {
              const m = CONVERSION_META[ev] || { label: ev, icon: FileText, color: 'text-slate-600 bg-slate-50' };
              return <Kpi key={ev} id={ev} label={m.label} value={n} icon={m.icon} color={m.color} />;
            })}
          </div>

          <Card className="border border-slate-200 shadow-sm mt-4" data-testid="analytics-series-chart">
            <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Daily views vs visitors</CardTitle></CardHeader>
            <CardContent className="pt-0 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4f46e5" stopOpacity={0.35} /><stop offset="95%" stopColor="#4f46e5" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} /><stop offset="95%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip labelFormatter={fmtDay} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Area type="monotone" dataKey="views" name="Views" stroke="#4f46e5" fill="url(#gViews)" strokeWidth={2} />
                  <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#f59e0b" fill="url(#gVisitors)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <BarList id="pages" title="Top pages" items={data.pages} total={data.pageviews} />
            <BarList id="sources" title="Top sources" items={data.sources} total={data.pageviews} />
            <BarList id="countries" title="Top countries" items={data.countries} total={data.pageviews} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <Card className="border border-slate-200 shadow-sm" data-testid="analytics-devices-chart">
              <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Devices</CardTitle></CardHeader>
              <CardContent className="pt-0 h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.devices} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Bar dataKey="count" name="Views" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            {Object.entries(data.top_lists || {}).map(([ev, items]) => (
              <BarList key={ev} id={`top-${ev}`} title={`${CONVERSION_META[ev]?.label || ev} — top items`} items={items} total={data.conversions?.[ev] || 0} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
